import type {FormKitNode, FormKitSchemaFormKit, FormKitSchemaNode} from '@formkit/core';
import {type Ref, toRef} from 'vue';
import {computed} from 'vue';
import type {MutateResult} from '@vue/apollo-composable';
import type {UseSofieMutationOptions, UseSofieMutationReturn} from './mutation';
import {useSofieMutation} from './mutation';

const formQuery = gql`
    query form($id: ID!) {
        form(id: $id) {
            schema: schema_v2
        }
    }
`;

const submitFormMutation = gql`
  mutation submitForm($id: ID!, $data: String!, $attachments: [FormFieldAttachment!]) {
    submitForm(form_id: $id, form_data: $data, attachments: $attachments)
  }
`;

type RawSchemaField = {
  label: string
  name: string
  type: string
  prefill?: string
  required?: boolean
  regex?: string
  ignore: boolean
  options?: Record<string, string>
  value?: string
}

export function useSofieForm(id: Ref<number> | number) {
  const query = useQuery(formQuery, {id}, {
    errorPolicy: 'all',
  });

  const form = computed(() => query.result.value?.form ?? {});
  const schema = computed<FormKitSchemaNode[]>(() => {
    if (!form.value?.schema) {
      return [];
    }

    return parseSchemaToFormkit(form.value?.schema);
  });

  return {
    ...query,
    form,
    schema,
  };
}

type FormFieldAttachment = {
  field_name: string
  attachment: File
}

export type FormVariables = {
  id: number
  data: object
  attachments: FormFieldAttachment[]
}

type RawFormVariables = {
  id: number
  data: string
  attachments: FormFieldAttachment[]
}

export type UseFormMutationReturn = Omit<UseSofieMutationReturn<FormData, FormVariables>, 'mutate'> & {
  mutate(variables: FormVariables, form?: FormKitNode): MutateResult<FormData>
};

export function useSofieFormMutation(
  options: UseSofieMutationOptions<FormData, FormVariables> = {},
): UseFormMutationReturn {
  const optionsRef = toRef(options);

  function transform(variables: FormVariables | null | undefined): RawFormVariables | null | undefined {
    if (variables) {
      return {
        id: variables.id,
        data: JSON.stringify(variables.data),
        attachments: variables.attachments,
      };
    }

    return variables;
  }

  const mutation = useSofieMutation<FormData, RawFormVariables>(submitFormMutation, computed(() => ({
    ...optionsRef.value,
    variables: transform(optionsRef.value.variables),
  })));

  return {
    ...mutation,
    mutate(variables: FormVariables, form?: FormKitNode) {
      return mutation.mutate(transform(variables), form);
    },
  };
}

function parseSchemaToFormkit(schema: RawSchemaField[]): FormKitSchemaNode[] {
  return schema.map((schema) => {
    switch (schema.type) {
      case 'attachment':
        return {
          ...schema,
          $formkit: 'file',
        } satisfies FormKitSchemaFormKit;
      case 'explanation':
        return {
          ...schema,
          $cmp: 'explanation',
          props: {
            __raw__value: schema.value,
          },
        } satisfies FormKitSchemaNode;
    }

    const field: FormKitSchemaNode = {
      ...schema,
      $formkit: schema.type,
    };

    delete field.type;

    if (field.options && Object.keys(field.options).length > 0 && ['checkbox', 'radio', 'select'].includes(schema.type)) {
      field.multiple = false;
    } else {
      delete field.options;
    }

    if (schema.required) {
      field.validation = 'required';
    }

    return field;
  });
}
